<script lang="ts">
export const elClipToAnchorPositions = [
  'bottom-left',
  'bottom',
  'bottom-right',
  'top-left',
  'top',
  'top-right',
  'left-start',
  'left',
  'left-end',
  'right-start',
  'right',
  'right-end',
] as const;
export type ElClipToAnchorPosition = (typeof elClipToAnchorPositions)[number];
</script>

<script lang="ts" setup>
import { useElementBounding, useElementVisibility, useWindowSize } from '@vueuse/core';
import { CSSProperties, onBeforeUnmount, onMounted, ref, watch, computed, nextTick } from 'vue';

const props = defineProps<{
  anchor: HTMLElement;
  matchAnchorWidth?: boolean;
  position: ElClipToAnchorPosition;
  minWidth?: number;
  distanceFromAnchor?: number;
}>();

const element = ref<HTMLElement | null>(null);
const elementStyle = ref<Partial<Pick<CSSProperties, 'top' | 'left' | 'width' | 'maxHeight' | 'visibility'>>>();

const { top: anchorTop, left: anchorLeft, width: anchorWidth, height: anchorHeight } = useElementBounding(props.anchor);
const { width: elementWidth, height: elementHeight } = useElementBounding(element);
const { width: rawWindowWidth, height: rawWindowHeight } = useWindowSize();

const isAnchorVisible = useElementVisibility(props.anchor);

// Calculate actual available window space accounting for scrollbars
const windowWidth = computed(() => {
  const hasVerticalScrollbar = document.documentElement.scrollHeight > document.documentElement.clientHeight;
  return hasVerticalScrollbar ? rawWindowWidth.value - getScrollbarWidth() : rawWindowWidth.value;
});

const windowHeight = computed(() => {
  const hasHorizontalScrollbar = document.documentElement.scrollWidth > document.documentElement.clientWidth;
  return hasHorizontalScrollbar ? rawWindowHeight.value - getScrollbarWidth() : rawWindowHeight.value;
});

const getScrollbarWidth = () => {
  // Create a temporary div with scrollbars
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  document.body.appendChild(outer);

  // Create an inner div to calculate the width difference
  const inner = document.createElement('div');
  outer.appendChild(inner);
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
};

const calculateElementStyle = async () => {
  // Wait for next tick to ensure the element is rendered and measurable
  await nextTick();

  // Calculate width using the natural width instead of elementWidth.value
  const baseWidth = props.matchAnchorWidth ? anchorWidth.value : elementWidth.value;

  const calculatedElementWidth = Math.min(
    props.minWidth && baseWidth < props.minWidth ? Math.max(props.minWidth, Math.min(elementWidth.value, anchorWidth.value)) : baseWidth,
    windowWidth.value,
  );

  const positions: Record<ElClipToAnchorPosition, { top: number; left: number }> = {
    'bottom-left': {
      top: anchorHeight.value + anchorTop.value + (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value - calculatedElementWidth + anchorWidth.value,
    },
    bottom: {
      top: anchorHeight.value + anchorTop.value + (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value + (anchorWidth.value - calculatedElementWidth) / 2,
    },
    'bottom-right': {
      top: anchorHeight.value + anchorTop.value + (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value,
    },
    'top-left': {
      top: anchorTop.value - elementHeight.value - (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value - calculatedElementWidth + anchorWidth.value,
    },
    top: {
      top: anchorTop.value - elementHeight.value - (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value + (anchorWidth.value - calculatedElementWidth) / 2,
    },
    'top-right': {
      top: anchorTop.value - elementHeight.value - (props.distanceFromAnchor ?? 0),
      left: anchorLeft.value,
    },
    'left-start': {
      top: anchorTop.value,
      left: anchorLeft.value - calculatedElementWidth - (props.distanceFromAnchor ?? 0),
    },
    left: {
      top: anchorTop.value + (anchorHeight.value - elementHeight.value) / 2,
      left: anchorLeft.value - calculatedElementWidth - (props.distanceFromAnchor ?? 0),
    },
    'left-end': {
      top: anchorTop.value + (anchorHeight.value - elementHeight.value),
      left: anchorLeft.value - calculatedElementWidth - (props.distanceFromAnchor ?? 0),
    },
    'right-start': {
      top: anchorTop.value,
      left: anchorLeft.value + anchorWidth.value + (props.distanceFromAnchor ?? 0),
    },
    right: {
      top: anchorTop.value + (anchorHeight.value - elementHeight.value) / 2,
      left: anchorLeft.value + anchorWidth.value + (props.distanceFromAnchor ?? 0),
    },
    'right-end': {
      top: anchorTop.value + (anchorHeight.value - elementHeight.value),
      left: anchorLeft.value + anchorWidth.value + (props.distanceFromAnchor ?? 0),
    },
  };

  const positionStyles = positions[props.position];

  // Calculate the max height of the element to avoid overflowing the window and overlapping the anchor when positioned below the anchor
  let maxHeight;
  switch (props.position) {
    case 'bottom-left':
    case 'bottom':
    case 'bottom-right':
      maxHeight = windowHeight.value - (anchorTop.value + anchorHeight.value);
      break;
  }

  // The left position should never overflow the window size
  elementStyle.value = {
    top: positionStyles.top + 'px',
    left: Math.min(Math.max(0, positionStyles.left), windowWidth.value - elementWidth.value) + 'px',
    width: calculatedElementWidth + 'px',
    maxHeight: maxHeight + 'px',
  };
};

watch([anchorTop, anchorLeft, anchorWidth, anchorHeight, elementWidth, elementHeight, windowWidth, windowHeight], calculateElementStyle);

// Using <Teleport to="body"> will break the <Transition> component (eg. <ElTooltip/>), a PR has been already merged but not yet released
// Issue: https://github.com/vuejs/core/issues/5836
// PR:    https://github.com/vuejs/core/pull/6548
onMounted(() => {
  document.body.appendChild(element.value!);
});

onBeforeUnmount(() => {
  element.value?.remove();
});
</script>

<template>
  <div
    ref="element"
    class="fixed z-50 max-h-screen max-w-screen overflow-auto w-auto"
    :style="{ ...elementStyle, visibility: isAnchorVisible ? 'visible' : 'hidden' }"
  >
    <slot v-bind="{ isVisible: isAnchorVisible }" />
  </div>
</template>
